import React, { useState, useEffect } from 'react';
import { Grid, FormControl, TextField, Autocomplete, IconButton, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import muiInputStyles from '../../../uiComponents/MuiInput/MuiInputStyles';
import './index.css';
import APIUtils from '../../../utils/APIUtils';
import { parse, isValid, startOfToday } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const MetadataMolecular = ({ selectedWorkflow, metadata, formValues, errors, handleUploadInputChange, onReady, navigationDirection, validateTextField, hasAttemptedNext, fieldPollingInterval }) => {
  const classes = muiInputStyles();
  const [instrument, setInstrument] = useState([]);
  const [variantCaller, setVariantCaller] = useState([]);
  const [reagentKit, setReagentKit] = useState([]);
  const [patientRace, setPatientRace] = useState([]);
  const [patientEthnicity, setPatientEthnicity] = useState([]);
  const [patientSex, setPatientSex] = useState([]);
  const [dateValue, setDateValue] = useState(
    formValues.patientDateOfBirth ? new Date(formValues.patientDateOfBirth) : null
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [localValues, setLocalValues] = useState({
    workflowName: formValues.workflowName || '',
    patientId: formValues.patientId || '',
    specimenId: formValues.specimenId || '',
  })
  const [localErrors, setLocalErrors] = useState({
    workflowName: '',
    patientId: '',
    specimenId: '',
  })

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [instrumentData, variantCallerData, reagentKitData, patientRaceData, patientEthnicityData, patientSexData] = await Promise.all([
          APIUtils.getMetadataValuesByField('Instrument'),
          APIUtils.getMetadataValuesByField('VariantCaller'),
          APIUtils.getMetadataValuesByField('ReagentKit'),
          APIUtils.getMetadataValuesByField('PatientRace'),
          APIUtils.getMetadataValuesByField('PatientEthnicity'),
          APIUtils.getMetadataValuesByField('PatientSex'),
        ]);

        // Sort dropdown options alphabetically by their display value
        const sortByValue = (arr) =>
          Array.isArray(arr)
            ? [...arr].sort((a, b) => (a.metadataValue || '').localeCompare(b.metadataValue || ''))
            : arr;

        setInstrument(sortByValue(instrumentData));
        setVariantCaller(sortByValue(variantCallerData));
        setReagentKit(sortByValue(reagentKitData));
        setPatientRace(patientRaceData);
        setPatientEthnicity(patientEthnicityData);
        setPatientSex(patientSexData);
      } catch (error) {
        console.error('Error fetching metadata:', error);
      }
    };

    fetchMetadata();
  }, []);

  useEffect(() => {
    // Check if all select dropdowns have loaded their data
    const selectsLoaded =
      instrument.length > 0 &&
      variantCaller.length > 0 &&
      reagentKit.length > 0 &&
      patientRace.length > 0 &&
      patientEthnicity.length > 0 &&
      patientSex.length > 0;

    // Call onReady as soon as selects are loaded (forward navigation)
    // or when all form values are loaded (backward navigation)
    const requiredFields = ['patientId', 'specimenId', 'instrument', 'variantCaller', 'reagentKit'];
    const allFormValuesLoaded = requiredFields.every(field => !!formValues[field]);

    if (selectsLoaded && (allFormValuesLoaded || navigationDirection !== 'back')) {
      onReady && onReady();
    }
  }, [
    navigationDirection,
    instrument,
    variantCaller,
    reagentKit,
    patientRace,
    patientEthnicity,
    patientSex,
    formValues,
    onReady
  ]);

  // Poll plain text inputs
  useEffect(() => {
    const interval = setInterval(() => {
      ['workflowName', 'patientId', 'specimenId'].forEach((field) => {
        const input = document.getElementById(field);
        if (input && input.value !== localValues[field]) {
          setLocalValues((prev) => ({ ...prev, [field]: input.value }));
          handleUploadInputChange({ target: { name: field, value: input.value } });
        }
      });
    }, fieldPollingInterval);

    return () => clearInterval(interval); // Clean up on unmount to avoid memory leaks
  }, []);

  // Poll date input separately
  useEffect(() => {
    const interval = setInterval(() => {
      const input = document.getElementById('patientDateOfBirth');
      if (input) {
        const parsed = parse(input.value, 'MM/dd/yyyy', new Date());
        const dateStr = dateValue ? dateValue.toDateString() : '';
        const inputDateStr = isValid(parsed) ? parsed.toDateString() : '';

        if (inputDateStr && inputDateStr !== dateStr) {
          setDateValue(parsed);
          handleUploadInputChange({ target: { name: 'patientDateOfBirth', value: input.value } });
        }
      }
    }, 300);

    return () => clearInterval(interval);
  }, [dateValue]);

  const handleDatePickerChange = (newValue) => {
    setDateValue(newValue);

    if (newValue && isValid(newValue)) {
      const minDate = new Date(1900, 0, 1);
      const today = startOfToday();

      if (newValue < minDate) {
        setErrorMessage('Please enter a valid date (MM/DD/YYYY) that is after 01/01/1900.');
        handleUploadInputChange({ target: { name: 'patientDateOfBirth', value: '' } });
        return;
      }
      if (newValue > today) {
        setErrorMessage('Date cannot be after today.');
        handleUploadInputChange({ target: { name: 'patientDateOfBirth', value: '' } });
        return;
      }
      setErrorMessage('');
      // Format to UTC yyyy-MM-dd for backend
      const utcDate = formatInTimeZone(newValue, 'UTC', 'yyyy-MM-dd');
      handleUploadInputChange({ target: { name: 'patientDateOfBirth', value: utcDate } });
    } else {
      setErrorMessage('Please enter a valid date (MM/DD/YYYY) that is today or earlier.');
      handleUploadInputChange({ target: { name: 'patientDateOfBirth', value: '' } });
    }
  };

  const handleClearDate = () => {
    setDateValue(null);
    setErrorMessage('');
    handleUploadInputChange({ target: { name: 'patientDateOfBirth', value: '' } });
  };

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setLocalValues((prev) => ({ 
      ...prev, 
      [name]: value
    }));
  };
    
  const handleTextBlur = (e) => {
    const { name, value } = e.target;
    const trimmedValue = value.trim();
    // Find the label for this field
    const fieldMeta = metadata.find(f => f.name === name);
    const label = fieldMeta ? fieldMeta.label : name;
    // Suppress the "required" message on blur until the user has attempted to
    // proceed at least once. Non-empty values are still validated on blur.
    const error = (trimmedValue.length === 0 && !hasAttemptedNext)
      ? ''
      : validateTextField(name, trimmedValue, label, selectedWorkflow);

    setLocalErrors((prev) => ({
      ...prev,
      [name]: error,
    }));

    // Update local state and parent with trimmed value
    setLocalValues((prev) => ({
      ...prev,
      [name]: trimmedValue,
    }));

    handleUploadInputChange({ target: { name, value: trimmedValue } });
  };

  const renderField = ({ key, label, name, required }) => {
    const isNonSelectField = ["workflowName", "patientId", "specimenId"].includes(name);
    const isDateField = name === "patientDateOfBirth";

    const fieldOptionsMap = {
      'instrument': instrument,
      'variantCaller': variantCaller,
      'reagentKit': reagentKit,
      'patientRace': patientRace,
      'patientEthnicity': patientEthnicity,
      'patientSex': patientSex
    };

    // Map stored Patient Sex codes to friendly display labels while keeping the
    // underlying value (M/F/U) submitted to the API unchanged.
    const patientSexLabelMap = { M: 'Male', F: 'Female', U: 'Unknown' };
    const getOptionLabel = (fieldName, value) =>
      fieldName === 'patientSex' ? (patientSexLabelMap[value] || value) : value;

    return (
      <div key={key}>
        <Grid container spacing={3} className="dashGridContainer">
          <Grid item xs={3}></Grid>
          <Grid item xs={6} sx={{ p: 1.5 }}>
            <FormControl fullWidth>
              {isDateField ? (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    label={label}
                    value={dateValue}
                    onChange={handleDatePickerChange}
                    maxDate={startOfToday()}
                    minDate={new Date(1900, 0, 1)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        name={name}
                        id={name}
                        className={classes.muiInputs}
                        required={required}
                        error={!!errorMessage || (!!errors && !!errors[name])}
                        helperText={errorMessage || (errors && errors[name])}
                        sx={{
                          '& .dobClearButton': { visibility: 'hidden' },
                          '&:hover .dobClearButton, &:focus-within .dobClearButton': { visibility: 'visible' },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {dateValue && (
                                <InputAdornment position="end">
                                  <IconButton
                                    className="dobClearButton"
                                    aria-label="Clear date of birth"
                                    size="small"
                                    edge="end"
                                    onClick={handleClearDate}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              )}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                        FormHelperTextProps={{ style: { color: "#DA190B" } }}
                      />
                    )}
                    PopperProps={{
                      'aria-label': 'Date picker dialog for date of birth',
                    }}
                  />
                </LocalizationProvider>
              ) : isNonSelectField ? (
                <TextField
                  fullWidth
                  size="small"
                  name={name}
                  id={name}
                  className={classes.muiInputs}
                  value={localValues[name] || ''}
                  onChange={handleTextChange}
                  onBlur={handleTextBlur}
                  variant="outlined"
                  label={label}
                  role="combobox" // MUI role modification for aria-required and additional required elements
                  aria-required={required}
                  aria-controls={name}
                  aria-expanded="false"
                  required={required}
                  helperText={errors[name] || localErrors[name]}
                  error={!!(errors[name] || localErrors[name])}
                  inputProps={{ maxLength: 25 }} 
                  FormHelperTextProps={{
                    style: { color: "#DA190B" },
                  }}
                />
              ) : (
                <Autocomplete
                  fullWidth
                  size="small"
                  id={name}
                  className={classes.muiInputs}
                  options={Array.isArray(fieldOptionsMap[name]) ? fieldOptionsMap[name] : []}
                  getOptionLabel={(option) => (option ? getOptionLabel(name, option.metadataValue) : '')}
                  isOptionEqualToValue={(option, selected) => option.metadataValue === selected.metadataValue}
                  value={
                    (Array.isArray(fieldOptionsMap[name])
                      ? fieldOptionsMap[name].find(option => option.metadataValue === formValues[name])
                      : null) || null
                  }
                  onChange={(event, newValue) =>
                    handleUploadInputChange({ target: { name, value: newValue ? newValue.metadataValue : '' } })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name={name}
                      variant="outlined"
                      label={label}
                      required={required}
                      error={!!errors && !!errors[name]}
                      helperText={errors && errors[name]}
                      inputProps={{
                        ...params.inputProps,
                        'aria-required': required,
                      }}
                      FormHelperTextProps={{
                        style: { color: "#DA190B" }
                      }}
                    />
                  )}
                />
              )}
            </FormControl>
          </Grid>
        </Grid>
      </div>
    );
  };

  return (
    <div className="Metadata">
      <form>{metadata.map(renderField)}</form>
    </div>
  );
};

export default MetadataMolecular;